import { useSyncExternalStore } from "react";

// ---------------------------------------------------------------------------
// Automation touches — the moment an automation changes a project a viewer
// can see. Every realtime issue event carries `automationRunId` when a run's
// step performed it; the subscription handlers record those here, and the
// row the event landed on wears a marker while the touch is fresh. The
// create flow watches its new project so the "Project created" toast can
// say an automation acted on it, in place, instead of a second toast.
// ---------------------------------------------------------------------------

export type AutomationTouchKind = "moved" | "updated";

export interface AutomationTouch {
  issueId: string;
  runId: string;
  kind: AutomationTouchKind;
  /** Epoch ms the event was recorded. */
  at: number;
}

/** How long a touch stays visible on its row. */
export const AUTOMATION_TOUCH_TTL_MS = 10_000;

type AutomationIssueEvent = {
  automationRunId?: string | null;
  issue: { issueId: string };
};

const touches = new Map<string, AutomationTouch>();
// The subscription registry runs every surface's handler for one wire event,
// so the same run reaches this store more than once — keyed to record once.
const seen = new Set<string>();
const listeners = new Set<() => void>();
const watchers = new Map<string, Set<(touch: AutomationTouch) => void>>();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Record a realtime issue event. A no-op unless a run performed it. */
export function recordAutomationTouch(
  event: AutomationIssueEvent,
  kind: AutomationTouchKind,
): void {
  const runId = event.automationRunId;
  if (!runId) return;
  const issueId = event.issue.issueId;
  const key = `${runId}:${issueId}:${kind}`;
  if (seen.has(key)) return;
  seen.add(key);

  const touch: AutomationTouch = { issueId, runId, kind, at: Date.now() };
  touches.set(issueId, touch);
  notify();
  setTimeout(() => {
    seen.delete(key);
    if (touches.get(issueId) === touch) {
      touches.delete(issueId);
      notify();
    }
  }, AUTOMATION_TOUCH_TTL_MS);

  const waiting = watchers.get(issueId);
  if (waiting) {
    watchers.delete(issueId);
    for (const onTouch of waiting) onTouch(touch);
  }
}

/**
 * Call `onTouch` once when an automation next touches `issueId`, or right
 * away if one already did within the TTL — the run can finish before the
 * mutation that triggered it answers. Gives up after `ttlMs`. Returns a
 * disposer.
 */
export function watchAutomationTouch(
  issueId: string,
  onTouch: (touch: AutomationTouch) => void,
  ttlMs: number = AUTOMATION_TOUCH_TTL_MS,
): () => void {
  const existing = touches.get(issueId);
  if (existing) {
    onTouch(existing);
    return () => {};
  }
  let set = watchers.get(issueId);
  if (!set) {
    set = new Set();
    watchers.set(issueId, set);
  }
  set.add(onTouch);
  const dispose = () => {
    const current = watchers.get(issueId);
    if (!current) return;
    current.delete(onTouch);
    if (current.size === 0) watchers.delete(issueId);
  };
  setTimeout(dispose, ttlMs);
  return dispose;
}

/** The fresh automation touch on a row, or null. */
export function useAutomationTouch(issueId: string): AutomationTouch | null {
  return useSyncExternalStore(
    subscribe,
    () => touches.get(issueId) ?? null,
    () => null,
  );
}

/** Test seam: forget every touch, dedupe key and watcher. */
export function resetAutomationTouchesForTests(): void {
  touches.clear();
  seen.clear();
  watchers.clear();
  notify();
}
